import { Col, Row } from "antd";
import Layout from "antd/lib/layout";
import Link from "next/link";
import { useRouter } from "next/router";
import { getActiveMenu } from "../../../helpers/getActiveMenu";
import { COLOR_VALUES } from "../../../lib/types/shared/enums";
import G_Image from "../Image/G_Image";
import { G_Text } from "../Text";

const { Sider } = Layout;

interface Props {
  menu: SingleMenu;
  isThisMenuActive: boolean;
}

interface SingleMenu {
  url: string;
  name: string;
  icon: string;
  urlKey?: string;
  active: boolean;
  subMenus?: Omit<SingleMenu, "subMenus">[];
}

/**
 * @since February 2024
 * @author Issa Jean Marie <jeanmarieissa@gmail.com>
 * @see {@link https://issadevs.com} - Author's website
 * @returns {JSX.Element}
 */

const SingleMenuItem = ({ menu, isThisMenuActive }: Props): JSX.Element => {
  const router = useRouter();
  const handleNavigate = (menu: SingleMenu) => {
    router.push({
      ...router,
      query: { ...router.query, active_tab: "" },
      pathname: menu.url
    });
  };

  return (
    <Link href={menu.url} key={menu.name} passHref>
      <Row
        className={`p-4 px-8 cursor-pointer relative ${
          isThisMenuActive ? "bg-ox_2_darker_blue" : "hover:bg-slate-800"
        }`}
        align="middle"
        gutter={12}
        onClick={() => handleNavigate(menu)}
      >
        <Col className="cursor-pointer flex items-center">
          <G_Image src={`/icons/${menu.icon}`} width={16} height={24} />
        </Col>

        <Col className="cursor-pointer">
          <G_Text
            dimension="normal"
            pointer
            weight={isThisMenuActive ? "semibold" : "normal"}
            color={isThisMenuActive ? "ox_2_primary" : "ox_2_white"}
          >
            {menu.name}
          </G_Text>
        </Col>
        {menu.subMenus && menu.subMenus[0] ? (
          <Col className="cursor-pointer group flex items-center justify-end flex-1">
            <G_Image
              src={`/icons/arrow-right-white.svg`}
              className="group-hover:hidden"
              height={24}
              width={16}
            />
            <Sider
              style={{
                background: COLOR_VALUES.ox_2_dark_blue
              }}
              className="overflow-auto hidden group-hover:inline absolute top-0 left-[200%] z-50"
              width={250}
            >
              {menu.subMenus.map((subMenu) => {
                const { isThisMenuActive: isThisSubMenuActive } = getActiveMenu(
                  {
                    menu: subMenu,
                    router
                  }
                );
                return (
                  <SingleMenuItem
                    menu={subMenu}
                    key={menu.name}
                    isThisMenuActive={isThisSubMenuActive}
                  />
                );
              })}
            </Sider>
          </Col>
        ) : null}
      </Row>
    </Link>
  );
};

export default SingleMenuItem;
